import * as React from 'react';
import * as Dialog from '@naan/primitives/modals/dialog';
import { observer } from 'mobx-react';
import { AppFactory } from 'app/app-factory';
import { styled } from '@naan/stitches.config';
import { Button } from '@naan/primitives/button';
import { paletteKey } from './study-palette';
import { ClockIcon } from '@naan/icons/clock-icon';
import { PointsIcon } from '@naan/icons/points-icon';
import { CheckmarkCircleExtraExtraLargeIcon } from '@naan/icons/checkmark-circle-icon';
import { HeadphonesExtraExtraLargeIcon } from '@naan/icons/headphones-icon';
import { VSpacer } from '@naan/primitives/spacer';
import { Text } from '@naan/primitives/text';
import { useReaction } from '@common/hooks/use-reaction';
import { SCRIPT_SCROLLING_CONTAINER_ID } from 'player/views/player-ui-components';
import { useReturnFromStudy } from 'lib/hooks/use-navigate-to-study';
import { useLineReconciler } from 'player/views/player-ui-model-setup';
import { msToPoints } from '@core/models/user-manager/listening-stats';
import { bugsnagNotify } from '@app/notification-service';
import { DialogHtmlContainer } from 'components/ui/dialog-html-container';
import { VStack } from '@naan/primitives/stack';

import __ from '@core/lib/localization';
import { scrollToCurrentLine } from './fx/scrolling';

const Wrapper = styled('div', {
  $$backgroundColor: paletteKey('$$endCard__backgroundColor'),
  $$primaryColor: paletteKey('$$endCard__primaryColor'),
  $$secondaryColor: paletteKey('$$endCard__secondaryColor'),
  $$dividerColor: paletteKey('$$endCard__dividerColor'),
  backgroundColor: '$$backgroundColor',
  color: '$$primaryColor',
  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.2)',
  borderRadius: '12px',
  padding: '0 16px 16px 16px',
  maxWidth: '600px',
  opacity: 0.3,
  transition: 'opacity 0.3s ease-out',
  marginTop: '24px',
  marginBottom: '24px',

  '& > .heading': {
    display: 'flex',
    padding: '72px 0',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textStyle: 'medium-heading',
    '& > .icon': {
      color: '$gray-400',
      '&.complete': {
        color: '$green-500',
      },
    },
  },

  '& > .row-title': {
    color: '$$secondaryColor',
    textStyle: 'tiny-text',
    textTransform: 'uppercase',
    borderTop: '1px solid $$dividerColor',
    paddingTop: '8px',
  },

  '& > .row': {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: '12px 0 8px 0',

    '& > .left': {
      display: 'flex',
      alignItems: 'center',
      textStyle: 'medium-heading',
      '& > .icon': {
        marginRight: '8px',
        '&.clock': {
          color: '$teal-500',
        },
        '&.points': {
          color: '$yellow-500',
        },
      },
    },
    '& > .right': {
      color: '$$secondaryColor',
    },
  },
  variants: {
    focused: {
      true: {
        opacity: 1,
      },
    },
  },
});

const SkipToEndConfirmationDialog = ({
  onDismiss,
  okAction,
}: {
  onDismiss: () => void;
  okAction: () => void;
}) => {
  const handleOkAndDismiss = React.useCallback(() => {
    onDismiss();
    okAction();
  }, [okAction, onDismiss]);

  return (
    <DialogHtmlContainer onDismiss={onDismiss}>
      <Dialog.HtmlHeading>
        {__('Skip to end of chapter', 'player.skipToEndOfChapter')}
      </Dialog.HtmlHeading>
      <Dialog.Body>
        <VStack>
          <Text>
            {__(
              'This will mark all sentences as played',
              'player.thisWillMarkAllSentencesAsPlayed'
            )}
          </Text>
          <VSpacer size={1} />
        </VStack>
      </Dialog.Body>
      <Dialog.ButtonsContainer>
        <Dialog.Button
          presentation={'cancel'}
          label={__('Cancel', 'common.cancel')}
          data-test-id="cancelButton"
          onClick={onDismiss}
          tabIndex={-1}
        />
        <Dialog.Button
          presentation={'teal'}
          label={__('Skip forward', 'player.skipForward')}
          data-test-id="okButton"
          onClick={handleOkAndDismiss}
          autoFocus
        />
      </Dialog.ButtonsContainer>
    </DialogHtmlContainer>
  );
};

export const EndOfChapter = observer(() => {
  const returnFromStudy = useReturnFromStudy();
  const model = AppFactory.studyModel;

  const handleCardClick = React.useCallback(() => {
    if (model.hasReachedFinalCard) {
      model.seekToFinalCard();
    } else {
      AppFactory.dialogPresenter.present(onDismiss => {
        return (
          <SkipToEndConfirmationDialog
            onDismiss={() => {
              onDismiss();
              /// @elliottjf, @frank this is not in the spec, but felt good to me as a cancel action
              scrollToCurrentLine();
            }}
            okAction={() => {
              model.seekToFinalCard();
            }}
          />
        );
      });
    }
  }, [model]);

  const { chapter } = model;
  if (!chapter) {
    return <>(missing chapter data)</>; // at least during development
  }

  const { story } = chapter;
  if (!chapter) {
    return <>(missing story data)</>;
  }

  const completed = model.completionReached;
  const reconciler = useLineReconciler();
  if (!reconciler) {
    bugsnagNotify('EndOfChapter - reconciler undefined');
    return null;
  }

  let className = reconciler.getJoinedMembershipStringForElement('OUTRO:');

  /// scroll to the end of the script whenever the card gains focus
  const ref = React.useRef<HTMLDivElement>(null);
  useReaction(
    () => model.completionReached && model.afterSpoken,
    gainedFocus => {
      if (gainedFocus) {
        const scriptPanel = document.getElementById(
          SCRIPT_SCROLLING_CONTAINER_ID
        );
        scriptPanel.scrollTo({
          top: scriptPanel.scrollHeight,
          behavior: 'smooth',
        });
      }
    }
  );

  return (
    <Wrapper
      className={className}
      focused={model.completionReached && model.afterSpoken}
      onClick={handleCardClick}
      id={'_OUTRO:'}
      ref={ref}
    >
      <h3 className="heading">
        {completed ? (
          <span className="complete icon">
            <CheckmarkCircleExtraExtraLargeIcon width={80} height={80} />
          </span>
        ) : (
          <span className="incomplete icon">
            <HeadphonesExtraExtraLargeIcon width={80} height={80} />
          </span>
        )}
        {completed
          ? __('Listen complete', 'player.listenComplete')
          : __('Listening…', 'player.listeningEllipsis')}
      </h3>
      <h5 className="row-title">
        {__('Time listened', 'player.timeListened')}
      </h5>
      <div className="row">
        <div className="left">
          <span className="clock icon">
            <ClockIcon />
          </span>
          {completed ? chapter.durationInWords : '--'}
        </div>
        <div className="right">{
          /*completed &&*/ `Story total: ${story.totalListenedInWords}`
        }</div>
      </div>
      <h5 className="row-title">
        {__('Points earned', 'player.pointsEarned')}
      </h5>
      <div className="row">
        <div className="left">
          <span className="points icon">
            <PointsIcon />
          </span>
          {/* {completed ? chapter.progressPoints : '--'} */}
          {completed ? msToPoints(model.furthestMillis) : '--'}
        </div>
        <div className="right">
          {__('Story total: %{total}', 'player.storyTotal', {
            total: story.totalPoints,
          })}
        </div>
      </div>
      <VSpacer size={3} />
      <Button
        label={__('Done', 'common.done')}
        onClick={returnFromStudy}
        presentation="teal"
        size="large"
        fullWidth
        disabled={!completed}
      />
    </Wrapper>
  );
});
